import { Controller } from "@hotwired/stimulus"

// Frappe Gantt (read-only): tasks come from `data-gantt-chart-tasks-value` JSON
// (see TimelineItems::GanttPayload). View mode buttons set data-gantt-chart-view-param.
export default class extends Controller {
  static values = { tasks: { type: Array, default: [] }, defaultView: String, readonly: { type: Boolean, default: true } }
  static targets = ["host"]

  connect() {
    this.gantt = null
    this._init()
  }

  disconnect() {
    this._destroyGantt()
  }

  // buttons: data-action="click->gantt-chart#showView" data-timeline-scale="Month"
  showView(event) {
    const mode = event.currentTarget.dataset.timelineScale
    if (!mode) return
    this.render(mode)
  }

  _init() {
    this._destroyGantt()

    const tasks = Array.isArray(this.tasksValue) ? this.tasksValue : []
    if (tasks.length === 0) return

    this.hostTarget.innerHTML = ""
    this.hostTarget.setAttribute("role", "group")
    this.hostTarget.setAttribute("aria-label", "Cronograma (gráfico de Gantt)")
    this.hostTarget.classList.toggle("gantt-surface--readonly", this.readonlyValue)

    requestAnimationFrame(() => {
      try {
        const defaultMode = this._normalizeView(this.defaultViewValue) || "Week"
        this.render(defaultMode)
      } catch (error) {
        this.hostTarget.innerHTML = `<p class="px-4 py-8 text-sm text-red-700">Não foi possível renderizar o gráfico de Gantt.</p>`
        console.error("Failed to render Gantt chart", error)
      }
    })
  }

  render(mode = "Week") {
    const tasks = this.tasksValue.map((task) => ({
      ...task,
      startDate: this._parseDate(task.start),
      endDate: this._parseDate(task.end)
    })).filter((task) => task.startDate && task.endDate)

    if (tasks.length === 0) return

    const view = this._normalizeView(mode) || "Week"
    const dayMs = 24 * 60 * 60 * 1000
    const minStart = new Date(Math.min(...tasks.map((task) => task.startDate.getTime())))
    const maxEnd = new Date(Math.max(...tasks.map((task) => task.endDate.getTime())))
    const start = this._addDays(minStart, -this._paddingDays(view))
    const end = this._addDays(maxEnd, this._paddingDays(view))
    const totalDays = Math.max(1, Math.ceil((end - start) / dayMs))
    const pxPerDay = this._pxPerDay(view)
    const leftPad = 24
    const topPad = 42
    const rowHeight = 38
    const width = Math.max(this.hostTarget.clientWidth || 720, leftPad + totalDays * pxPerDay + 24)
    const height = topPad + tasks.length * rowHeight + 28

    const ticks = this._ticks(start, end, view)
    const tickLines = ticks.map((tick) => {
      const x = leftPad + Math.round(((tick.date - start) / dayMs) * pxPerDay)
      return `
        <line x1="${x}" y1="0" x2="${x}" y2="${height}" class="stroke-zinc-200" />
        <text x="${x + 4}" y="18" class="fill-zinc-500 text-[11px]">${tick.label}</text>
      `
    }).join("")

    const rows = tasks.map((task, index) => {
      const y = topPad + index * rowHeight
      const x = leftPad + Math.round(((task.startDate - start) / dayMs) * pxPerDay)
      const days = Math.max(1, Math.ceil((task.endDate - task.startDate) / dayMs) + 1)
      const barWidth = Math.max(12, days * pxPerDay)
      return `
        <rect x="0" y="${y - 10}" width="${width}" height="${rowHeight}" class="${index % 2 === 0 ? "fill-white" : "fill-zinc-50"}" />
        <rect x="${x}" y="${y}" width="${barWidth}" height="18" rx="5" class="fill-blue-500" />
        <text x="${x + 8}" y="${y + 13}" class="fill-white text-[11px] font-medium">${this._escape(task.name)}</text>
        <text x="${Math.min(x + barWidth + 8, width - 220)}" y="${y + 13}" class="fill-zinc-600 text-[11px]">${this._formatDate(task.startDate)} - ${this._formatDate(task.endDate)}</text>
      `
    }).join("")

    this.hostTarget.innerHTML = `
      <div class="overflow-x-auto">
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" class="block min-w-full">
          <rect x="0" y="0" width="${width}" height="${height}" class="fill-white" />
          ${tickLines}
          ${rows}
        </svg>
      </div>
    `
  }

  _normalizeView(v) {
    if (!v) return null
    // Accept "Month" or "Gantt.VIEW_MODE.MONTH" style from Ruby
    const map = {
      "Quarter Day": "Quarter Day",
      "Half Day": "Half Day",
      Day: "Day",
      Week: "Week",
      Month: "Month",
      Year: "Year"
    }
    return map[v] || v
  }

  _destroyGantt() {
    this.gantt = null
    if (this.hasHostTarget) this.hostTarget.innerHTML = ""
  }

  _parseDate(value) {
    if (!value) return null
    const [year, month, day] = value.split("-").map(Number)
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
  }

  _addDays(date, days) {
    const next = new Date(date)
    next.setDate(next.getDate() + days)
    return next
  }

  _pxPerDay(view) {
    return { Day: 28, Week: 10, Month: 3.5, Year: 0.7 }[view] || 10
  }

  _paddingDays(view) {
    return { Day: 3, Week: 14, Month: 45, Year: 180 }[view] || 14
  }

  _ticks(start, end, view) {
    const ticks = []
    const cursor = new Date(start)
    const step = { Day: 1, Week: 7, Month: 30, Year: 365 }[view] || 7

    while (cursor <= end) {
      ticks.push({ date: new Date(cursor), label: this._tickLabel(cursor, view) })
      cursor.setDate(cursor.getDate() + step)
    }

    return ticks
  }

  _tickLabel(date, view) {
    if (view === "Year") return `${date.getFullYear()}`
    if (view === "Month") return date.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
  }

  _formatDate(date) {
    return date.toLocaleDateString("pt-BR")
  }

  _escape(value) {
    const div = document.createElement("div")
    div.textContent = value || ""
    return div.innerHTML
  }
}
